import { useContext, useState, useMemo, useEffect } from 'react';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty } from 'lodash';
import { getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';

const useDecompositionWaterfallData = (isWaterfallChartEnabled: boolean) => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData } = profitPoolConfig;

    const selectedKPI = 'profit_growth_absolute';
    const selectedSubFilter = useMemo(() => {
        return defaultAttributeForData;
    }, [defaultAttributeForData]);

    const [decompositionWaterfallData, setDecompositionWaterfallData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    // Get Previous year Data
    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    const {
        data: previousYearData,
        isPending: isPreviousYearDataPending,
        isError: isPreviousYearDataError,
        isSuccess: isPreviousYearDataSuccess,
    } = useQuery({
        queryKey: [
            'previous-year-decomposition-waterfall-data',
            selectedSubFilter,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                offset: 0,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: true,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                limit: PROFIT_POOL_API_LIMIT,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                isWaterfallChartEnabled,
        ),
    });

    // Get Current year Data
    const {
        data: currentYearData,
        isPending: isCurrentYearDataPending,
        isError: isCurrentYearDataError,
        isSuccess: isCurrentYearDataSuccess,
    } = useQuery({
        queryKey: [
            'current-year-decomposition-waterfall-data',
            selectedSubFilter,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                offset: 0,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                limit: PROFIT_POOL_API_LIMIT,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                isWaterfallChartEnabled,
        ),
    });

    // Get WaterFallChartData

    const {
        data: attributeData,
        isPending: isAttributeDataPending,
        isError: isAttributeDataError,
        isSuccess: isAttributeDataSuccess,
        fetchNextPage: fetchAttributeDataNextPage,
        hasNextPage: hasNextAttributeDataPage,
        isFetchingNextPage: isFetchingAttributeDataNextPage,
    } = useInfiniteQuery({
        queryKey: [
            'decomposition-waterfall-infi',
            selectedSubFilter,
            selectedKPI,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                limit: PROFIT_POOL_API_LIMIT,
            });
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                isWaterfallChartEnabled,
        ),
    });

    useEffect(() => {
        if (!isAttributeDataPending && attributeData?.pages) {
            const allData = attributeData.pages.flatMap((page: any) => page?.data || []);

            if (!isEmpty(allData)) {
                let minProfit = Number.POSITIVE_INFINITY;
                let maxProfit = Number.NEGATIVE_INFINITY;

                const finalChartData = allData.map((item: any) => {
                    const currentProfit = item.kpi_values?.[selectedKPI] || 0;
                    if (currentProfit < minProfit) minProfit = currentProfit;
                    if (currentProfit > maxProfit) maxProfit = currentProfit;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        [selectedKPI]: currentProfit,
                        tier: item.tier,
                    };
                });

                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);

                setDecompositionWaterfallData(finalChartData);
            } else {
                setDecompositionWaterfallData([]);
                setMin(0);
                setMax(0);
            }
        }
    }, [isAttributeDataPending, attributeData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(
            decompositionWaterfallData,
            previousYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
            currentYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
            selectedKPI,
        );
    }, [decompositionWaterfallData, previousYearData, currentYearData, selectedKPI]);

    return {
        data: decompositionWaterfallData,
        currentYearData: currentYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
        previousYearData: previousYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        isPending: isPreviousYearDataPending || isCurrentYearDataPending || isAttributeDataPending,
        isError: isPreviousYearDataError || isCurrentYearDataError || isAttributeDataError,
        isSuccess: isPreviousYearDataSuccess && isCurrentYearDataSuccess && isAttributeDataSuccess,
        fetchNextPage: fetchAttributeDataNextPage,
        hasNextPage: hasNextAttributeDataPage,
        isFetchingNextPage: isFetchingAttributeDataNextPage,
    };
};

export default useDecompositionWaterfallData;
